'use client';

import { getFullName } from '@/models/user/user';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { createContext, useCallback, useEffect, useMemo } from 'react';

import { useSearchParams } from 'next/navigation';

import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { FormFillingSkeleton } from '@/shared/page-loading-skeletons';
import { formFilledByUser, getFormFillingDetails } from '@/shared/services/forms';
import {
	IFormFilledByUserPayload,
	IGetFormFillingDetailsPayload,
} from '@/shared/services/forms/forms.types';
import { getUserDetails } from '@/shared/services/user';
import {
	emitMixpanelEvent,
	identifyUserAndSetEmailOnMixpanel,
} from '@/shared/utils/mixpanel-utils';

import { getApiErrorDetails } from '@/utils/axios';
import {
	FORM_FILLED_BY_USER_QUERY_KEY,
	GET_FORM_FILLING_DETAILS_QUERY_KEY,
	GET_USER_DETAILS_QUERY_KEY,
} from '@/utils/constants';

import ApiError from '../api-error';
import { IFormFillPageContext, IFormFillPageProviderProps } from './form-fill-page-provider.types';

export const FormFillPageContext = createContext<IFormFillPageContext>({
	updatedFormFillDetails: null,
	updatedUserDetails: null,
	updatedFormFilledByUserDetails: null,
	invalidateUserRelatedQueries: async () => {
		return {
			updatedUserDetailsRes: null,
			updatedFormFilledByUserDetailsRes: null,
		};
	},
	refetchFormFillDetails: async () => null,
	isUserDetailsRefetching: false,
	isFormFilledByUserDetailsRefetching: false,
	resetUserRelatedQueries: async () => {},
});

export function FormFillPageProvider({
	children,
	isTokenExist,
	formId,
}: IFormFillPageProviderProps) {
	const searchParams = useSearchParams();
	const queryClient = useQueryClient();

	const formFillingDetailsQueryKey = useMemo(
		() => [GET_FORM_FILLING_DETAILS_QUERY_KEY, formId],
		[formId]
	);
	const userDetailsQueryKey = useMemo(() => [GET_USER_DETAILS_QUERY_KEY], []);
	const formFilledByUserQueryKey = useMemo(() => [FORM_FILLED_BY_USER_QUERY_KEY, formId], [formId]);

	const {
		data: userDetails,
		refetch: refetchUserDetails,
		isRefetching: isUserDetailsRefetching,
	} = useQuery({
		queryKey: userDetailsQueryKey,
		queryFn: () => getUserDetails(),
		enabled: isTokenExist,
	});

	const {
		data: formFilledByUserDetails,
		refetch: refetchFormFilledByUserDetails,
		isRefetching: isFormFilledByUserDetailsRefetching,
	} = useQuery({
		queryKey: formFilledByUserQueryKey,
		queryFn: ({ queryKey }) => {
			const [, formId] = queryKey;

			const payload: IFormFilledByUserPayload = {
				form_id: formId,
			};

			return formFilledByUser(payload);
		},
		enabled: !!userDetails,
	});

	const {
		data: formFillDetails,
		isLoading: isFormFillDetailsLoading,
		isError: isFormFillDetailsError,
		error: formFillDetailsError,
		refetch: refetchFormFillDetailsQuery,
	} = useQuery({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: formFillingDetailsQueryKey,
		queryFn: ({ queryKey }) => {
			const [, formId] = queryKey;

			const initialReferrer = document.referrer ?? '';
			const initialReferrerDomain = initialReferrer ? new URL(initialReferrer).host : '';

			const payload: IGetFormFillingDetailsPayload = {
				register_view: searchParams.get('sidfss') === 'true' ? false : undefined,
				form_id: String(formId),
				initial_referrer: initialReferrer,
				initial_referrer_domain: initialReferrerDomain,
			};

			return getFormFillingDetails(payload);
		},
		enabled: isTokenExist ? !!userDetails && !!formFilledByUserDetails : true,
	});

	const isLoading = isFormFillDetailsLoading || !formFillDetails;

	const invalidateUserRelatedQueries = useCallback(async () => {
		try {
			const userDetailsResponse = await refetchUserDetails();
			const formFilledByUserResponse = await refetchFormFilledByUserDetails();

			return {
				updatedUserDetailsRes: userDetailsResponse.data ?? null,
				updatedFormFilledByUserDetailsRes: formFilledByUserResponse.data ?? null,
			};
		} catch (error) {
			return {
				updatedUserDetailsRes: null,
				updatedFormFilledByUserDetailsRes: null,
			};
		}
	}, [refetchUserDetails, refetchFormFilledByUserDetails]);

	const refetchFormFillDetails = useCallback(async () => {
		try {
			const response = await refetchFormFillDetailsQuery();
			return response.data ?? null;
		} catch (error) {
			return null;
		}
	}, [refetchFormFillDetailsQuery]);

	const handleResetUserRelatedQueries = useCallback(async () => {
		queryClient.setQueryData(userDetailsQueryKey, () => {
			return null;
		});
		queryClient.setQueryData(formFilledByUserQueryKey, () => {
			return null;
		});
	}, [queryClient, userDetailsQueryKey, formFilledByUserQueryKey]);

	const value = useMemo(() => {
		return {
			updatedFormFillDetails: formFillDetails ?? null,
			updatedUserDetails: userDetails ?? null,
			updatedFormFilledByUserDetails: formFilledByUserDetails ?? null,
			invalidateUserRelatedQueries,
			refetchFormFillDetails,
			isUserDetailsRefetching,
			isFormFilledByUserDetailsRefetching,
			resetUserRelatedQueries: handleResetUserRelatedQueries,
		};
	}, [
		formFillDetails,
		userDetails,
		formFilledByUserDetails,
		invalidateUserRelatedQueries,
		refetchFormFillDetails,
		isUserDetailsRefetching,
		isFormFilledByUserDetailsRefetching,
		handleResetUserRelatedQueries,
	]);

	useEffect(() => {
		if (!!userDetails) {
			identifyUserAndSetEmailOnMixpanel({
				email: userDetails.email,
				name: getFullName(userDetails),
			});
		}
	}, [userDetails]);

	useEffect(() => {
		if (isFormFillDetailsError) {
			emitMixpanelEvent(mixpanelEvents.VIEWED_FORM_NOT_FOUND, {
				feature: MIXPANEL_APP_FEATURES.FORM,
				form_id: formId,
				form_name: formFillDetails?.name,
				error_message: formFillDetailsError?.message,
			});
		}
	}, [isFormFillDetailsError, formId, formFillDetails?.name, formFillDetailsError?.message]);

	if (isFormFillDetailsError) {
		const errorDetails = getApiErrorDetails(formFillDetailsError);

		return (
			<main className="h-screen w-screen">
				<ApiError errorDetails={errorDetails} />
			</main>
		);
	}

	if (isLoading) {
		return <FormFillingSkeleton />;
	}

	return (
		<FormFillPageContext.Provider value={value}>
			<main>{children}</main>
		</FormFillPageContext.Provider>
	);
}
